import type { AccountCandidate } from "@/features/identity/identity.api";

interface AccountPickerProps {
  /// Why the person is choosing, e.g. "Your Google email is on more than one
  /// Pulsewise account."
  reason: string;
  candidates: ReadonlyArray<AccountCandidate>;
  pending: boolean;
  error?: string | null;
  onPick: (userId: string) => void;
}

/// "Pick an account" for an email that belongs to several accounts. Shared by
/// the Google, password-less code and magic-link sign-ins. Purely
/// presentational: the caller finishes the sign-in and navigates.
export function AccountPicker({ reason, candidates, pending, error, onPick }: AccountPickerProps) {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tighter">Pick an account</h1>
        <p className="mt-2 text-sm text-text-secondary">{reason} Choose which one to sign in to.</p>
      </div>
      <ul className="flex flex-col gap-2">
        {candidates.map((c) => (
          <li key={c.userId}>
            <button
              type="button"
              onClick={() => onPick(c.userId)}
              disabled={pending}
              className="flex w-full items-center justify-between rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:border-border-strong hover:shadow-xs"
            >
              <div>
                <div className="text-sm font-semibold">{c.accountName || "Untitled account"}</div>
                <div className="font-mono text-2xs tracking-wider text-text-tertiary">
                  Sign in as {c.displayName}
                </div>
              </div>
              <span className="text-text-tertiary">→</span>
            </button>
          </li>
        ))}
      </ul>
      {error ? (
        <p role="alert" className="text-sm text-destructive-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}
